
import { Team } from "../types";
import { TeamCard } from "./TeamCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Users, Shield } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

interface TeamListProps {
  teams: Team[] | undefined;
  isLoading: boolean;
  isAdmin: boolean;
  onDeleteTeam: (team: Team) => Promise<void>;
}

export const TeamList = ({ teams, isLoading, isAdmin, onDeleteTeam }: TeamListProps) => {
  const { t } = useLanguage();
  
  // Debug: log teams data to see its structure
  console.log("TeamList in teams page received:", teams);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, index) => (
          <Card key={index} className={cn(
            "group relative overflow-hidden transform transition-all duration-500",
            "bg-gradient-to-br from-background/95 via-background/90 to-muted/50 border-primary/20",
            "backdrop-blur-sm rounded-2xl"
          )}>
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10 pointer-events-none"></div>
            <div className="relative p-6">
              <Skeleton className="w-12 h-12 rounded-xl mb-4" />
              <div className="space-y-2">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-1/3" />
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <Skeleton className="h-9" />
                  <Skeleton className="h-9" />
                </div>
                <Skeleton className="h-12 mt-2" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (!teams || teams.length === 0) {
    return (
      <Card className="bg-gradient-to-br from-muted/30 to-muted/50 border-primary/20">
        <CardContent className="text-center py-16">
          <div className="flex flex-col items-center space-y-6">
            <div className="p-6 bg-gradient-to-br from-primary/10 to-primary/20 rounded-2xl">
              <Users className="h-16 w-16 text-primary" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-muted-foreground">
                {isAdmin ? t('noTeamsCreated') : t('noTeamAssignments')}
              </h3>
              <p className="text-muted-foreground/70">
                {isAdmin 
                  ? t('createFirstTeam')
                  : t('notMemberOfTeams')}
              </p>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary/10 to-primary/20 backdrop-blur-sm rounded-full border border-primary/30 shadow-sm">
                <Shield className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-primary">{t('administratorAccess')}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {teams.map((team, index) => (
        <div 
          key={team.id}
          style={{ animationDelay: `${index * 100}ms` }}
          className="animate-fadeIn"
        >
          <TeamCard
            team={team}
            isAdmin={isAdmin}
            onDeleteTeam={onDeleteTeam}
          />
        </div>
      ))}
    </div>
  );
};
